import { useState } from 'react';
import { useHomeLayout } from '../../lib/home-store.js';
import { Tile } from './Tile.js';
import { WidgetCard } from './Widgets.js';
import { EditPanel } from './EditPanel.js';
import { useBadgeCounts } from './useBadgeCounts.js';
import { useEditMode } from './edit-context.js';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../lib/auth.js';
import { filterByAllowed } from '@sfp/shared/permissions';
import { useEntityTiles } from './useEntityTiles.js';

export function HomeBase() {
  const { layout, save, resetToDefault } = useHomeLayout();
  const { editing, setEditing } = useEditMode();
  const counts = useBadgeCounts();
  const [zoom, setZoom] = useState(1);
  const navigate = useNavigate();
  const { role, allowedTileGroupIds } = useAuth();
  const entityTiles = useEntityTiles();

  // Admin sees every group; otherwise apply the allowed-id filter.
  // Edit mode also bypasses filtering so admins can re-permission groups.
  const visibleGroups =
    role === 'admin' || editing ? layout.groups : filterByAllowed(layout.groups, allowedTileGroupIds);

  const badgeForTile = (source: string): number => {
    if (source === 'triage') return counts.triage;
    if (source === 'bills_due_week') return counts.bills_due_week;
    if (source === 'bills_past_due') return counts.bills_past_due;
    if (source === 'documents_total') return counts.documents_total;
    return 0;
  };

  return (
    <div className="relative min-h-full">
      <div className="mx-auto flex max-w-[1600px] gap-6 px-6 py-4" style={{ fontSize: `${zoom * 100}%` }}>
        {/* Main grid of tile groups */}
        <div className="flex-1">
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3">
            {visibleGroups.map((group) => {
              const isDynamic = group.dynamic === 'entities';
              const groupTiles = isDynamic ? entityTiles : group.tiles;
              return (
              <div
                key={group.id}
                className="rounded-3xl bg-[#FFFFFF] p-5 ring-1 ring-stone-200/80 shadow-sm"
              >
                <div className="grid grid-cols-3 justify-items-center gap-y-4 gap-x-1 sm:grid-cols-3">
                  {groupTiles.map((t) => (
                    <Tile
                      key={t.id}
                      tile={t}
                      editing={editing && !isDynamic}
                      badgeValue={
                        t.badge?.kind === 'count' ? badgeForTile(t.badge.source) : undefined
                      }
                      onEdit={() => setEditing(true)}
                      onRemove={() => {
                        const groups = layout.groups.map((g) =>
                          g.id === group.id ? { ...g, tiles: g.tiles.filter((x) => x.id !== t.id) } : g,
                        );
                        save({ ...layout, groups });
                      }}
                    />
                  ))}
                  {isDynamic && groupTiles.length === 0 && (
                    <div className="col-span-3 px-2 py-4 text-center text-[11px] text-stone-400">
                      No entities yet. Seed them in Entities.
                    </div>
                  )}
                  {editing && !isDynamic && (
                    <button
                      type="button"
                      onClick={() => {
                        const groups = layout.groups.map((g) =>
                          g.id === group.id
                            ? {
                                ...g,
                                tiles: [
                                  ...g.tiles,
                                  {
                                    id: `tile_${Math.random().toString(36).slice(2, 8)}`,
                                    label: 'New tile',
                                    glyph: '+',
                                    color: 'slate' as const,
                                    href: '/',
                                  },
                                ],
                              }
                            : g,
                        );
                        save({ ...layout, groups });
                      }}
                      className="grid h-14 w-14 place-items-center rounded-xl border-2 border-dashed border-stone-400 text-2xl text-stone-400 hover:border-stone-700 hover:text-stone-700"
                    >
                      +
                    </button>
                  )}
                </div>
                <div className="mt-4 text-center text-sm font-semibold text-stone-800">
                  {group.title}
                  {isDynamic && (
                    <span className="ml-1 text-[10px] font-normal text-stone-400">(auto)</span>
                  )}
                </div>
              </div>
              );
            })}
            {editing && (
              <button
                type="button"
                onClick={() => {
                  save({
                    ...layout,
                    groups: [
                      ...layout.groups,
                      { id: `g_${Math.random().toString(36).slice(2, 8)}`, title: 'New group', tiles: [] },
                    ],
                  });
                }}
                className="grid min-h-[180px] place-items-center rounded-3xl border-2 border-dashed border-stone-300 text-sm text-stone-400 hover:border-stone-600 hover:text-stone-700"
              >
                + Add group
              </button>
            )}
          </div>
        </div>

        {/* Right column */}
        <div className="hidden w-72 shrink-0 space-y-4 xl:block">
          {layout.rightColumn.map((w) => (
            <WidgetCard
              key={w.id}
              widget={w}
              counts={counts}
              editing={editing}
              onEdit={() => setEditing(true)}
              onRemove={() => save({ ...layout, rightColumn: layout.rightColumn.filter((x) => x.id !== w.id) })}
            />
          ))}
          {editing && (
            <button
              type="button"
              onClick={() => setEditing(true)}
              className="grid h-24 w-full place-items-center rounded-2xl border-2 border-dashed border-stone-300 text-xs text-stone-400 hover:border-stone-600 hover:text-stone-700"
            >
              + Add widget
            </button>
          )}
        </div>
      </div>

      {/* zoom control — bottom-right */}
      <div className="fixed bottom-4 right-4 flex items-center gap-1 rounded-full bg-white/90 px-2 py-1 text-xs text-stone-600 shadow ring-1 ring-stone-200">
        <button onClick={() => setZoom((z) => Math.max(0.7, z - 0.05))} className="px-1">−</button>
        <span className="tabular-nums">{Math.round(zoom * 100)}%</span>
        <button onClick={() => setZoom((z) => Math.min(1.4, z + 0.05))} className="px-1">+</button>
      </div>

      {editing && (
        <EditPanel
          layout={layout}
          onChange={save}
          onClose={() => setEditing(false)}
          onReset={() => {
            resetToDefault();
            navigate('/', { replace: true });
          }}
        />
      )}
    </div>
  );
}
